/**
 * brandm26-points-redemption.js
 * 用途: brandm26-points-redemption 组件交互逻辑（简化版）
 *   - 登录状态检测与 Banner 切换
 *   - 商品组数据获取（每个 group 独立 productType 调 getProductsNew）
 *   - 卡片渲染（API 命中 → 覆盖 fallback；未命中 → 隐藏）
 *   - View All / Collapse 切换（按 window.innerWidth 决定可见数）
 *   - 2 个弹框：Confirm Redemption（蓝框1）、Reward Unlocked（蓝框2）
 *   - 兑换接口调用 + 错误码映射
 * 依赖: momcozy-login.liquid（已渲染 login-dialog 组件）
 *
 * DOM 结构约定（由 sections/brandm26-points-redemption.liquid 输出）:
 *   - [data-bmpr-section]              section 根
 *   - [data-bmpr-coins-unlogged]       未登录 banner
 *   - [data-bmpr-coins-logged]         登录 banner（含 [data-bmpr-coins-value]）
 *   - [data-bmpr-login-trigger]        登录按钮
 *   - [data-bmpr-group]                商品组容器（含 data-product-type/ids/pc-visible/mb-visible/view-all-label/collapse-label）
 *   - [data-bmpr-card]                 商品卡（含 data-product-id、data-fallback-xxx、data-required-points、data-product-code 等）
 *   - [data-bmpr-redeem-btn]           卡内兑换按钮
 *   - [data-bmpr-view-all-btn]         View All 按钮
 *   - [data-bmpr-overlay="confirm"|"success"]  弹框容器
 *   - [data-bmpr-confirm-main] / [data-bmpr-success-main]  弹框主标题（{item_name}/{cozycoins} 占位）
 *   - [data-bmpr-confirm-btn] / [data-bmpr-success-btn]    弹框按钮
 *
 * 设计原则:
 *   - 大量复用 member-promotion-cozy.js 的 API 工具函数（独立 IIFE 副本）
 *   - 使用 var 保持 IE11 兼容（项目约定）
 *   - 防重入：window.__BMPR_INITIALIZED__ 哨兵
 */
(function () {
  "use strict";

  // --------------------------------------------------------------------------
  // 禁用浏览器自动恢复滚动位置
  // 否则登录刷新后浏览器会把页面滚回刷新前的位置（可能是其它 section），
  // 覆盖掉 scrollToSectionIfFromLogin 的显式滚动。
  // --------------------------------------------------------------------------
  if ("scrollRestoration" in history) {
    history.scrollRestoration = "manual";
  }

  // --------------------------------------------------------------------------
  // 防重入保护
  // --------------------------------------------------------------------------
  if (window.__BMPR_INITIALIZED__) {
    console.warn("[BMPR] Script already loaded, skip re-initialization.");
    return;
  }
  window.__BMPR_INITIALIZED__ = true;

  // --------------------------------------------------------------------------
  // 骨架屏：隐藏指定 section 的页面级骨架屏
  //   1. 设 data-bmpr-ready="true" 触发 CSS 淡出动画
  //   2. 350ms 后彻底移除 DOM 节点，释放内存
  // 参考 member-promotion-cozy.liquid 的 hideSectionPageSkeleton
  // --------------------------------------------------------------------------
  function hideBmprPageSkeleton(sectionEl) {
    try {
      if (!sectionEl) return;
      if (sectionEl.getAttribute("data-bmpr-ready") === "true") return;
      sectionEl.setAttribute("data-bmpr-ready", "true");
      var skel = sectionEl.querySelector("[data-bmpr-page-skeleton]");
      if (skel) {
        setTimeout(function () {
          if (skel && skel.parentNode) skel.parentNode.removeChild(skel);
        }, 350);
      }
    } catch (e) {
      /* noop */
    }
  }

  // --------------------------------------------------------------------------
  // 工具函数
  // --------------------------------------------------------------------------

  function getCookie(name) {
    var cookies = document.cookie.split("; ");
    var found = cookies.find(function (c) {
      return c.startsWith(name + "=");
    });
    return found
      ? decodeURIComponent(found.split("=").slice(1).join("="))
      : null;
  }

  function isLoggedIn() {
    try {
      var config = window.__MC__CONFIG__;
      if (!config || !config.cookieName) return false;
      var token = getCookie(config.cookieName);
      return !!(token && token.length > 0);
    } catch (e) {
      return false;
    }
  }

  /**
   * 检测登录来源标记：登录/注册成功刷新后，自动滚动到该组件
   * 参考 member-promotion-cozy.js scrollToSectionIfFromLogin
   */
  function scrollToSectionIfFromLogin(sectionEl, sectionId) {
    try {
      var raw = sessionStorage.getItem("bmpr_login_source");
      if (!raw) return;

      var data;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        sessionStorage.removeItem("bmpr_login_source");
        return;
      }

      var FIVE_MIN = 5 * 60 * 1000;
      if (
        !data ||
        data.sectionId !== sectionId ||
        Date.now() - (data.ts || 0) >= FIVE_MIN
      ) {
        sessionStorage.removeItem("bmpr_login_source");
        return;
      }

      if (!isLoggedIn()) {
        sessionStorage.removeItem("bmpr_login_source");
        return;
      }

      sessionStorage.removeItem("bmpr_login_source");
      setTimeout(function () {
        try {
          // 优先滚到具体的卡片（Redeem 点击触发时记录了 productId）
          if (data.productId) {
            var card = sectionEl.querySelector(
              '[data-bmpr-card][data-product-id="' +
                data.productId.replace(/"/g, '\\"') +
                '"]',
            );
            if (card) {
              card.scrollIntoView({ behavior: "smooth", block: "center" });
              return;
            }
          }
          // 兜底：滚到 section 顶部（login-trigger 按钮走这条）
          sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
        } catch (e) {
          /* noop */
        }
      }, 300);
    } catch (e) {
      /* noop */
    }
  }

  /**
   * 打开登录弹框
   * 极简写法：与 sep-brand26-claim-component.js / member-promotion-cozy.js 一致，
   * 直接调 loginDialog.open()，不做轮询 / .click() 兜底 / 4 层 activityType 注入。
   *
   * 假设 <login-dialog> 模块（momcozy-login.es.js）已加载，
   * 否则 .open 不是 function，整个函数静默 return 即可（参考 sep-brand26-claim-component.js L146-160）。
   */
  function openLoginDialog(activityType) {
    var loginDialog = document.querySelector("login-dialog");
    if (loginDialog && typeof loginDialog.open === "function") {
      loginDialog.open(null, {
        activityType: activityType,
        mustAgreeEmail: true,
      });
    }
  }

  function debounce(fn, delay) {
    var timer = null;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(ctx, args);
      }, delay);
    };
  }

  // --------------------------------------------------------------------------
  // API
  // --------------------------------------------------------------------------

  /**
   * 构造统一请求头
   */
  function buildApiHeaders(extra) {
    var headers = { "Content-Type": "application/json" };
    if (window.__MC__CONFIG__ && window.__MC__CONFIG__.headers) {
      Object.keys(window.__MC__CONFIG__.headers).forEach(function (k) {
        headers[k] = window.__MC__CONFIG__.headers[k];
      });
    }
    try {
      var config = window.__MC__CONFIG__;
      if (config && config.cookieName) {
        var token = getCookie(config.cookieName);
        if (token) headers["Authorization"] = "Bearer " + token;
      }
    } catch (e) {}
    if (extra) {
      Object.keys(extra).forEach(function (k) {
        headers[k] = extra[k];
      });
    }
    return headers;
  }

  /**
   * 获取用户总积分（复用 getProductsNew 接口，pageSize=1）
   */
  function getUserPoints() {
    return fetch(
      buildApiBase() +
        "/api/station/point/getProductsNew?productType=7&pageSize=1",
      { method: "GET", headers: buildApiHeaders() },
    )
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        var total = 0;
        if (data && data.code === "200" && data.result) {
          total =
            typeof data.result.points === "number" ? data.result.points : 0;
        } else if (data && typeof data.points === "number") {
          total = data.points;
        }
        return { total: total };
      });
  }

  /**
   * 获取指定 productType 的可兑换商品列表
   * @param {number|string} productType
   * @returns {Promise<{code, result, message?}>}
   */
  function getProducts(productType) {
    var types = String(productType || "")
      .split(",")
      .map(function (s) {
        return s.trim();
      })
      .filter(Boolean)
      .join(",");
    var url =
      buildApiBase() +
      "/api/station/point/getProductsNew?productType=" +
      (types || "7") +
      "&pageSize=1000";
    return fetch(url, { method: "GET", headers: buildApiHeaders() }).then(
      function (res) {
        if (!res.ok) throw new Error("getProducts failed: " + res.status);
        return res.json();
      },
    );
  }

  /**
   * 执行积分兑换
   * POST /api/station/point/exchangeProductNew
   */
  function exchangeProduct(productCode, number, usedPoints) {
    return fetch(buildApiBase() + "/api/station/point/exchangeProductNew", {
      method: "POST",
      headers: buildApiHeaders(),
      body: JSON.stringify({
        productCode: productCode,
        number: typeof number === "number" && number > 0 ? number : 1,
        usedPoints: typeof usedPoints === "number" ? usedPoints : 0,
      }),
    }).then(function (res) {
      return res.json().then(function (data) {
        if (!res.ok) {
          var httpErr = new Error(data.message || "Redeem failed");
          httpErr.serverMsg = data.message;
          httpErr.serverCode = data.code;
          throw httpErr;
        }
        if (String(data.code) !== "200") {
          var bizErr = new Error(data.message || "Redeem failed");
          bizErr.serverMsg = data.message;
          bizErr.serverCode = data.code;
          throw bizErr;
        }
        // redeemStatus: 0 成功，1 失败（业务层失败，如积分不足、库存不足等）
        var resultData = data.result || data;
        var redeemStatus =
          resultData.redeemStatus !== undefined &&
          resultData.redeemStatus !== null
            ? String(resultData.redeemStatus)
            : "0";
        if (redeemStatus === "1") {
          var redeemErr = new Error(
            resultData.desc || data.message || "Redeem failed",
          );
          redeemErr.redeemStatus = 1;
          redeemErr.serverMsg = resultData.desc || data.message;
          redeemErr.serverCode = data.code;
          redeemErr.redeemData = resultData;
          throw redeemErr;
        }
        return data;
      });
    });
  }

  function buildApiBase() {
    return (
      (window.__MC__CONFIG__ && window.__MC__CONFIG__.apiUrl) ||
      "https://account.momcozy.com"
    );
  }

  // --------------------------------------------------------------------------
  // 弹框管理（openDialog / closeDialog + focus trap）
  // --------------------------------------------------------------------------

  function getFocusableElements(dialogEl) {
    if (!dialogEl) return [];
    var selector =
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    var nodes = dialogEl.querySelectorAll(selector);
    var result = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (n.offsetWidth === 0 && n.offsetHeight === 0) continue;
      if (n.getAttribute("aria-hidden") === "true") continue;
      result.push(n);
    }
    return result;
  }

  function installFocusTrap(dialogEl, triggerEl) {
    if (!dialogEl) return function () {};
    if (triggerEl) {
      try {
        dialogEl.__bmprTriggerEl = triggerEl;
      } catch (e) {}
    }
    var onKeyDown = function (e) {
      if (e.key !== "Tab") return;
      var focusable = getFocusableElements(dialogEl);
      if (!focusable.length) {
        e.preventDefault();
        dialogEl.focus();
        return;
      }
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !dialogEl.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !dialogEl.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    dialogEl.addEventListener("keydown", onKeyDown);
    var rafId = requestAnimationFrame(function () {
      var focusable = getFocusableElements(dialogEl);
      var closeBtn = dialogEl.querySelector(
        ".bmpr-dialog__close, [data-bmpr-close]:first-of-type",
      );
      var target =
        closeBtn && focusable.indexOf(closeBtn) !== -1
          ? closeBtn
          : focusable[0] || dialogEl;
      try {
        target.focus();
      } catch (e) {}
    });
    return function uninstall() {
      cancelAnimationFrame(rafId);
      dialogEl.removeEventListener("keydown", onKeyDown);
      var trig = dialogEl.__bmprTriggerEl;
      if (trig && document.contains(trig)) {
        try {
          trig.focus();
        } catch (e) {}
      }
      dialogEl.__bmprTriggerEl = null;
    };
  }

  function openDialog(dialogEl, triggerEl) {
    if (!dialogEl) return;
    if (typeof dialogEl.__bmprUninstallFocus === "function") {
      dialogEl.__bmprUninstallFocus();
      dialogEl.__bmprUninstallFocus = null;
    }
    dialogEl.style.display = "flex";
    requestAnimationFrame(function () {
      dialogEl.classList.add("is-open");
    });
    document.body.style.overflow = "hidden";
    dialogEl.__bmprUninstallFocus = installFocusTrap(dialogEl, triggerEl);

    function onKey(e) {
      if (e.key === "Escape") {
        closeDialog(dialogEl);
        document.removeEventListener("keydown", onKey);
      }
    }
    document.addEventListener("keydown", onKey);
    // 这一期 banner 蓝框只支持点 X / ESC 关闭，不点蒙层关闭
    // 如需启用蒙层关闭：在 config.enable_overlay_close_mobile 控制
  }

  function closeDialog(dialogEl) {
    if (!dialogEl) return;
    dialogEl.classList.remove("is-open");
    dialogEl.style.display = "none";
    if (typeof dialogEl.__bmprUninstallFocus === "function") {
      dialogEl.__bmprUninstallFocus();
      dialogEl.__bmprUninstallFocus = null;
    }
    var openDialogs = document.querySelectorAll(".bmpr-overlay.is-open");
    if (!openDialogs.length) {
      document.body.style.overflow = "";
    }
  }

  // --------------------------------------------------------------------------
  // Token 替换：{item_name} {cozycoins}
  // --------------------------------------------------------------------------

  function replaceTokens(template, ctx) {
    if (!template) return "";
    return String(template)
      .replace(/\{item_name\}/g, ctx.itemName || "")
      .replace(
        /\{cozycoins\}/g,
        ctx.cozycoins != null ? ctx.cozycoins.toLocaleString() : "0",
      )
      .replace(/\{need\}/g, ctx.need != null ? ctx.need.toLocaleString() : "0")
      .replace(/\{have\}/g, ctx.have != null ? ctx.have.toLocaleString() : "0");
  }

  // --------------------------------------------------------------------------
  // 主初始化
  // --------------------------------------------------------------------------

  function initSection(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    var config =
      (window.__BMPR_CONFIG__ && window.__BMPR_CONFIG__[sectionId]) || {};

    var cardRedeemLabel = config.cardRedeemLabel || "Redeem";
    var cardOutOfStockLabel = config.cardOutOfStockLabel || "Out of Stock";
    var pointsUnit = config.pointsUnit || " Pts";
    var insufficientMsgTpl =
      config.insufficientMsgTpl ||
      "Insufficient CozyCoins. You need {need} but only have {have}.";
    // 优先从 section 的 data-activity-type 读（与 sep-brand26-claim 一致），回退到 config，最后默认
    var registerSource =
      sectionEl.dataset.activityType ||
      config.registerSource ||
      "BDMemberCozycoinsEvent";
    // section 级兜底图/标题：API 没返回、或 block 没配 fallback_* 时用
    // 注意：不加 var，直接赋值给模块级变量（上面声明的 defaultCardImage/defaultCardTitle）
    defaultCardImage = config.defaultCardImage || "";
    defaultCardTitle = config.defaultCardTitle || "";

    // 默认错误码文案（来自 member-promotion-cozy，可被 config.redeemErrorMessages 覆盖）
    var defaultRedeemErrorMessages = {
      10004251: "Redeeming, please do not submit again.",
      10004252: "Product does not exist.",
      10004253: "This product is currently unavailable for redemption.",
      10004254: "Product has been taken offline.",
      10004255: "Insufficient membership level, please upgrade first.",
      10004256: "Insufficient CozyCoins.",
      10004257: "Out of stock.",
      10004258: "You've reached the redemption limit for this product.",
      10004259: "Redemption failed, please try again later.",
      10004506: "Please bind your email first.",
      10004512: "Limit: 1 per day, max 3 per month (category restriction 1).",
      10004513: "Limit: 1 per day, max 3 per month (category restriction 2).",
    };
    var userRedeemErrorMessages = {};
    try {
      if (typeof config.redeemErrorMessages === "string") {
        var parsed = JSON.parse(config.redeemErrorMessages);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          userRedeemErrorMessages = parsed;
        }
      } else if (
        config.redeemErrorMessages &&
        typeof config.redeemErrorMessages === "object" &&
        !Array.isArray(config.redeemErrorMessages)
      ) {
        userRedeemErrorMessages = config.redeemErrorMessages;
      }
    } catch (e) {
      console.warn(
        "[BMPR] Invalid redeemErrorMessages JSON, using defaults.",
        e,
      );
    }
    var redeemErrorMessages = Object.assign(
      {},
      defaultRedeemErrorMessages,
      userRedeemErrorMessages,
    );

    // DOM 引用
    var bannerUnloggedEl = sectionEl.querySelector(
      "[data-bmpr-coins-unlogged]",
    );
    var bannerLoggedEl = sectionEl.querySelector("[data-bmpr-coins-logged]");
    var coinsValueEl = sectionEl.querySelector("[data-bmpr-coins-value]");
    var confirmDlgEl = document.getElementById("bmpr-confirm-" + sectionId);
    var successDlgEl = document.getElementById("bmpr-success-" + sectionId);
    var infoDlgEl = document.getElementById("bmpr-info-" + sectionId);

    // 状态
    var userTotalPoints = 0;
    var userPointsLoaded = false; // 标志：当前页面会话内是否成功加载过积分
    var pendingRedeem = null; // { productCode, points, name, image }
    // 缓存 loadAllProducts 拉到的全量商品列表，供后续兑换失败/重新打开 confirm 时直接查表，不再发新请求
    var cachedProductList = [];

    // ---- 关闭按钮绑定 ----
    [confirmDlgEl, successDlgEl, infoDlgEl].forEach(function (dlg) {
      if (!dlg) return;
      dlg.querySelectorAll("[data-bmpr-close]").forEach(function (btn) {
        btn.addEventListener("click", function () {
          closeDialog(dlg);
        });
      });
    });

    // ---- Banner ----
    function showLoggedBanner() {
      if (bannerUnloggedEl) bannerUnloggedEl.style.display = "none";
      if (bannerLoggedEl) bannerLoggedEl.style.display = "";
    }
    function showUnloggedBanner() {
      if (bannerUnloggedEl) bannerUnloggedEl.style.display = "";
      if (bannerLoggedEl) bannerLoggedEl.style.display = "none";
    }

    function loadUserPoints() {
      return getUserPoints()
        .then(function (data) {
          if (data && typeof data.total === "number") {
            userTotalPoints = data.total;
            userPointsLoaded = true;
            if (coinsValueEl)
              coinsValueEl.textContent = userTotalPoints.toLocaleString();
            refreshAllCards();
          } else {
            // API 没正常返回 total（可能是 401 未登录）
            userTotalPoints = 0;
            userPointsLoaded = false;
          }
          return data;
        })
        .catch(function (err) {
          console.warn("[BMPR] Failed to load user points:", err);
          userPointsLoaded = false;
        });
    }

    function initLoginState() {
      if (isLoggedIn()) {
        showLoggedBanner();
        loadUserPoints();
      } else {
        showUnloggedBanner();
      }
    }

    // 登录事件：momcozy-login.es.js 在登录态下挂载时会派发 "keep" 事件，无需重复加载积分
    document.addEventListener("LUTE:AUTH:LOGIN", function (e) {
      showLoggedBanner();
      if (e && e.detail && e.detail.type === "keep") return;
      loadUserPoints();
    });

    // 未登录按钮：触发登录弹框（与 sep-brand26-claim-component.js L143-144 一致用 .onclick）
    sectionEl
      .querySelectorAll("[data-bmpr-login-trigger]")
      .forEach(function (btn) {
        btn.onclick = function () {
          try {
            sessionStorage.setItem(
              "bmpr_login_source",
              JSON.stringify({ sectionId: sectionId, ts: Date.now() }),
            );
          } catch (e) {
            /* noop */
          }
          openLoginDialog(registerSource);
        };
      });

    // ---- 卡片渲染 ----
    // section 级兜底图/标题（initSection 中从 config 读取）
    var defaultCardImage = "";
    var defaultCardTitle = "";

    function updateCard(cardEl, apiData) {
      if (!apiData) {
        cardEl.setAttribute("data-hidden", "");
        return;
      }
      cardEl.removeAttribute("data-hidden");
      var localizedTitle = cardEl.dataset.productTitle || "";
      var fallbackTitle = cardEl.dataset.fallbackTitle || "";
      var cardImage = cardEl.dataset.cardImage || "";
      var fallbackImage = cardEl.dataset.fallbackImage || "";
      var fallbackPoints = parseInt(cardEl.dataset.fallbackPoints, 10) || 0;

      // 标题/图片兜底链：API 返回 → block 的 fallback_* → section 的 defaultCard*
      //   - localizedTitle: 来自 Shopify product.title（在 _redeem_product.liquid 中设置）
      //   - apiData.productName: 后端 API 返回的商品名
      //   - fallbackTitle: block 配置的 fallback_title
      //   - defaultCardTitle: section 级兜底（运营在 section schema 配）
      var productTitle =
        localizedTitle ||
        apiData.productName ||
        fallbackTitle ||
        defaultCardTitle;
      // 外部卡片图片优先级：page-configured（block fallback_image > section default） > API
      //   - fallbackImage: block 配置的 fallback_image（最高，运营强制覆盖）
      //   - defaultCardImage: section 级兜底
      //   - apiData.productImageNew / productImage: 后端 API 返回的图片
      // 注：Confirm 弹框单独有 block.card_image 字段，逻辑见 openConfirm
      var productImage =
        fallbackImage ||
        defaultCardImage ||
        apiData.productImageNew ||
        apiData.productImage;

      // 图片
      var imgWrap = cardEl.querySelector(".bmpr-group__card-img-wrap");
      if (imgWrap && productImage) {
        var altText = productTitle || fallbackTitle || "Product image";
        var img = cardEl.querySelector(".bmpr-group__card-img");
        if (img) {
          img.src = productImage;
          img.srcset = productImage;
          img.alt = altText;
        } else {
          // 保留已有的 overlay 图，只插入主图
          var overlayHtml = imgWrap.innerHTML;
          imgWrap.innerHTML =
            '<img class="bmpr-group__card-img" src="' +
            escapeAttr(productImage) +
            '" srcset="' +
            escapeAttr(productImage) +
            '" alt="' +
            escapeHtml(altText) +
            '" loading="lazy" decoding="async" width="300" height="300">' +
            overlayHtml;
        }
      }

      // 标题
      var titleEl = cardEl.querySelector(".bmpr-group__card-title");
      if (titleEl) titleEl.textContent = productTitle;

      // 积分
      var apiPoints =
        typeof apiData.exchangePoint === "number" &&
        !isNaN(apiData.exchangePoint)
          ? apiData.exchangePoint
          : null;
      var resolvedPoints = apiPoints != null ? apiPoints : fallbackPoints;
      var pointsNumEl = cardEl.querySelector("[data-bmpr-card-points]");
      if (pointsNumEl) {
        pointsNumEl.textContent =
          resolvedPoints > 0 ? resolvedPoints.toLocaleString() + " " + pointsUnit : "—";
      }

      // 写 data 属性供后续状态判断使用
      cardEl.dataset.productCode =
        apiData.productCode !== undefined && apiData.productCode !== null
          ? String(apiData.productCode)
          : "";
      cardEl.dataset.requiredPoints = resolvedPoints;
      cardEl.dataset.productCount =
        typeof apiData.productCount === "number" ? apiData.productCount : 0;
      cardEl.dataset.usedCount =
        typeof apiData.usedCount === "number" ? apiData.usedCount : 0;
      cardEl.dataset.userLimitCount =
        typeof apiData.userLimitCount === "number" ? apiData.userLimitCount : 0;
      cardEl.dataset.redeemLimitReached =
        parseInt(cardEl.dataset.usedCount, 10) >=
        parseInt(cardEl.dataset.userLimitCount, 10)
          ? "1"
          : "0";
      cardEl.dataset.redemptionStatus =
        apiData.redemptionStatus !== undefined &&
        apiData.redemptionStatus !== null
          ? String(apiData.redemptionStatus)
          : "1";

      // 缓存 productTitle 到按钮上（用于 aria-label）
      var btn = cardEl.querySelector("[data-bmpr-redeem-btn]");
      if (btn) btn.__bmprProductTitle = productTitle;
    }

    function setCardBtnActive(btn, label) {
      btn.textContent = label;
      btn.classList.remove("bmpr-group__card-btn--disabled");
      btn.classList.add("bmpr-group__card-btn--active");
      btn.disabled = false;
      btn.removeAttribute("aria-disabled");
      if (
        btn.__bmprPrevAriaLabel !== undefined &&
        btn.__bmprPrevAriaLabel !== null
      ) {
        if (btn.__bmprPrevAriaLabel) {
          btn.setAttribute("aria-label", btn.__bmprPrevAriaLabel);
        } else {
          btn.removeAttribute("aria-label");
        }
      }
      btn.__bmprPrevAriaLabel = null;
    }

    function setCardBtnDisabled(btn, label) {
      btn.textContent = label;
      btn.classList.add("bmpr-group__card-btn--disabled");
      btn.classList.remove("bmpr-group__card-btn--active");
      btn.disabled = true;
      btn.setAttribute("aria-disabled", "true");
      btn.__bmprPrevAriaLabel = btn.getAttribute("aria-label") || "";
      var reason = label === cardOutOfStockLabel ? "Out of stock" : label;
      var productName = btn.__bmprProductTitle || "";
      btn.setAttribute(
        "aria-label",
        (productName ? productName + " — " : "") + reason + " (unavailable)",
      );
    }

    function refreshSingleCardBtn(cardEl) {
      var btn = cardEl.querySelector("[data-bmpr-redeem-btn]");
      if (!btn) return;

      // 优先用卡片自己的 redeem_label 配置（每张卡可独立配置按钮文案），
      // 没有时才回退到 section 级配置 card_redeem_label
      var perCardLabel = String(
        cardEl.dataset.bmprCardRedeemLabel || "",
      ).trim();
      var btnLabel = perCardLabel || cardRedeemLabel;

      // 跟登录状态无关，统一用 redemptionStatus 判断按钮状态（参考 member-promotion-cozy.js）：
      //   1 = 可兑换 → 激活（点击时再判断是否弹登录）
      //   2 = 积分不足 → 置灰，文案用 btnLabel
      //   4 = 库存不足 → 置灰，文案用 section 的 card_out_of_stock_label
      //   7 = 已达上限 → 置灰，文案用 btnLabel
      //   兜底：未知 status 视为不可兑换
      var status = parseInt(cardEl.dataset.redemptionStatus, 10);

      if (status === 4) {
        setCardBtnDisabled(btn, cardOutOfStockLabel);
        return;
      }
      if (status === 2 || status === 7) {
        setCardBtnDisabled(btn, btnLabel);
        return;
      }
      if (status !== 1) {
        console.warn(
          "[BMPR] Card",
          cardEl.dataset.productId,
          "has unrecognized redemptionStatus:",
          status,
        );
        setCardBtnDisabled(btn, btnLabel);
        return;
      }

      // 1 = 可兑换（不管是否登录，按钮可点；点击时由外层 click handler 判断弹登录还是弹 Confirm）
      setCardBtnActive(btn, btnLabel);
    }

    function refreshAllCards() {
      sectionEl.querySelectorAll("[data-bmpr-card]").forEach(function (card) {
        if (!card.hasAttribute("data-hidden")) {
          refreshSingleCardBtn(card);
        }
      });
    }

    // ---- 商品列表加载（整 section 一次性请求） ----
    // 不管配了几个 _brandm26-points-group，把所有 [data-bmpr-card] 的 product_type
    // 收集去重后合成一次 productType 列表，只发一次 getProductsNew；
    // 返回后再按 productId 在所有 group 的所有 card 上做匹配 -> updateCard，
    // 最后再按 group 调 setupViewAll 初始化 View All/Collapse。
    // 整页初始化期间就只发两次接口：1) getUserPoints  2) getProducts
    function loadAllProducts() {
      var allCards = sectionEl.querySelectorAll("[data-bmpr-card]");
      if (!allCards.length) return null;

      // 收集整 section 的 product_type 去重（默认 '7'）
      var typeSet = new Set();
      allCards.forEach(function (c) {
        var t = String(c.dataset.productType || "7").trim();
        if (t) typeSet.add(t);
      });
      var productTypes = Array.from(typeSet).sort().join(",") || "7";
      console.log("[BMPR] loadAllProducts, combined productTypes:", productTypes);

      return getProducts(productTypes)
        .then(function (res) {
          if (res && res.code === "200" && res.result) {
            // 顺手更新用户积分（同接口返回 result.points）
            if (typeof res.result.points === "number") {
              userTotalPoints = res.result.points;
              if (coinsValueEl)
                coinsValueEl.textContent = userTotalPoints.toLocaleString();
            }
            var list = Array.isArray(res.result.pointProductRespList)
              ? res.result.pointProductRespList
              : [];
            cachedProductList = list; // 缓存给后续查表用

            // 7 → shopifyProductId；其他 → productCode
            var apiMapShopify = {};
            var apiMapCode = {};
            list.forEach(function (item) {
              if (!item) return;
              if (item.shopifyProductId) {
                apiMapShopify[String(item.shopifyProductId)] = item;
              }
              if (item.productCode != null && item.productCode !== "") {
                apiMapCode[String(item.productCode)] = item;
              }
            });
            // 在整 section 范围内匹配所有 card
            allCards.forEach(function (card) {
              var pid = String(card.dataset.productId || "").trim();
              var ctype = String(card.dataset.productType || "7");
              // 双 key 兜底：primary 查不到时再试 secondary
              //   type 7:   primary = shopifyProductId,  fallback = productCode
              //   其他:     primary = productCode,       fallback = shopifyProductId
              // 兼容运营把 product_type 配错的情况（不影响正常配置）
              var primaryHit =
                ctype === "7" ? apiMapShopify[pid] : apiMapCode[pid];
              var fallbackHit =
                ctype === "7" ? apiMapCode[pid] : apiMapShopify[pid];
              updateCard(card, primaryHit || fallbackHit || null);
            });
            refreshAllCards();
            // 各组分别初始化 View All（按 group 的 pcVisible / mbVisible）
            sectionEl
              .querySelectorAll("[data-bmpr-group]")
              .forEach(setupViewAll);
          } else {
            console.warn("[BMPR] getProducts non-200:", res);
            cachedProductList = [];
            // 失败：所有 card 走 fallback，保持按钮可点
            allCards.forEach(function (card) {
              card.removeAttribute("data-hidden");
              refreshSingleCardBtn(card);
            });
            sectionEl
              .querySelectorAll("[data-bmpr-group]")
              .forEach(setupViewAll);
          }
        })
        .catch(function (err) {
          console.warn("[BMPR] getProducts failed:", err);
          cachedProductList = [];
          allCards.forEach(function (card) {
            card.removeAttribute("data-hidden");
            refreshSingleCardBtn(card);
          });
          sectionEl
            .querySelectorAll("[data-bmpr-group]")
            .forEach(setupViewAll);
        });
    }

    // ---- View All / Collapse ----
    function getVisibleCount(groupEl) {
      var pc = parseInt(groupEl.dataset.pcVisible, 10) || 4;
      var mb = parseInt(groupEl.dataset.mbVisible, 10) || 2;
      return window.innerWidth <= 1024 ? mb : pc;
    }

    function setupViewAll(groupEl) {
      // 总是先调 applyVisibility，保证标题/按钮显隐永远是最新的（包括 0 卡片场景）
      applyVisibility(groupEl);
      var cards = groupEl.querySelectorAll("[data-bmpr-card]");
      if (!cards.length) {
        return;
      }
      var btn = groupEl.querySelector("[data-bmpr-view-all-btn]");
      if (!btn) return;
      btn.addEventListener("click", function () {
        var expanded = btn.getAttribute("aria-expanded") === "true";
        if (expanded) {
          groupEl.classList.remove("bmpr-group--expanded");
          btn.setAttribute("aria-expanded", "false");
          btn.textContent = groupEl.dataset.viewAllLabel || "View All";
        } else {
          groupEl.classList.add("bmpr-group--expanded");
          btn.setAttribute("aria-expanded", "true");
          btn.textContent = groupEl.dataset.collapseLabel || "Collapse";
        }
        // issue 2 修复：toggle class 后必须重算 visibility，否则 style.display 没更新
        applyVisibility(groupEl);
      });
    }

    // ---- View All / Collapse ----
    function getVisibleCount(groupEl) {
      var pc = parseInt(groupEl.dataset.pcVisible, 10) || 4;
      var mb = parseInt(groupEl.dataset.mbVisible, 10) || 2;
      return window.innerWidth <= 1024 ? mb : pc;
    }

    function applyVisibility(groupEl) {
      var cards = groupEl.querySelectorAll("[data-bmpr-card]");
      // 统计当前未隐藏（已匹配）的卡片数
      var nonHiddenCount = 0;
      cards.forEach(function (c) {
        if (!c.hasAttribute("data-hidden")) nonHiddenCount++;
      });

      // 已展开时不裁剪
      if (groupEl.classList.contains("bmpr-group--expanded")) {
        cards.forEach(function (c) {
          // data-hidden 卡片保持 display:none，其余全部显示
          // grid 自动布局会把可见卡从左到右填充，空槽自然在右侧
          c.style.display = c.hasAttribute("data-hidden") ? "none" : "";
        });
        var wrapExpanded = groupEl.querySelector("[data-bmpr-view-all-wrap]");
        if (wrapExpanded) wrapExpanded.style.display = "none";
      } else {
        // 未展开：按 visible 裁剪（跳过 data-hidden 卡片）
        var visible = getVisibleCount(groupEl);
        var visibleIdx = 0;
        cards.forEach(function (c) {
          if (c.hasAttribute("data-hidden")) {
            c.style.display = "none";
            return;
          }
          c.style.display = visibleIdx < visible ? "" : "none";
          if (visibleIdx < visible) visibleIdx++;
        });
        // 按钮显隐：只有非隐藏卡片数 > visible 时才显示
        var wrap = groupEl.querySelector("[data-bmpr-view-all-wrap]");
        if (wrap) {
          wrap.style.display = nonHiddenCount > visible ? "" : "none";
        }
      }

      // 标题显隐：没有任何未隐藏卡片时隐藏整个组的标题
      var title = groupEl.querySelector(".bmpr-group__title");
      if (title) {
        title.style.display = nonHiddenCount === 0 ? "none" : "";
      }
    }

    var onResize = debounce(function () {
      sectionEl.querySelectorAll("[data-bmpr-group]").forEach(applyVisibility);
    }, 150);
    window.addEventListener("resize", onResize);

    // ---- 兑换按钮点击 ----
    sectionEl.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-bmpr-redeem-btn]");
      if (!btn) return;
      // 已禁用按钮不响应
      if (btn.disabled) return;
      var card = btn.closest("[data-bmpr-card]");
      if (!card) return;
      handleRedeemClick(card);
    });

    /**
       * 处理 Redeem 点击
       * 关键设计：页面必须在加载时确认过登录状态（initLoginState 跑过）才允许进入兑换流程。
       *   - 用户在别的 tab 登录后回到本页面没刷新：cookie 存在但本页面 userPointsLoaded=false
       *     → 仍按「未登录」处理，弹登录框让用户在本页重新登录/确认
       *   - 避免出现「页面状态陈旧 → 误判为已登录 → 直接打开 Confirm」的体验
       */
      function handleRedeemClick(card) {
        // 未登录 OR 本页面会话内未确认过登录状态 → 一律弹登录
        if (!isLoggedIn() || !userPointsLoaded) {
          // 记录本次点击的卡片位置，登录刷新后滚回这张卡（而不是只滚到 section 顶部）
          try {
            sessionStorage.setItem(
              "bmpr_login_source",
              JSON.stringify({
                sectionId: sectionId,
                productId: String(card.dataset.productId || ""),
                ts: Date.now(),
              }),
            );

            // 把 URL hash 同步成当前 section 的 anchor。
            // 原因：用户可能从其它 section 的锚点进入本页面（如 #brand-day-earn），
            // 点击 Redeem 后登录对话框会把 location.href 当作 redirect 传给后端，
            // 后端原样返回，登录刷新后浏览器会按这个 hash 滚到错误的位置。
            // 改成本 section 的 anchor 后，refresh 进来的 URL 就有正确 hash，
            // 再配合 scrollToSectionIfFromLogin 滚到具体卡片。
            var sectionEl = document.querySelector(
              '[data-section-id="' + sectionId + '"]',
            );
            if (sectionEl && sectionEl.id && location.hash !== "#" + sectionEl.id) {
              history.replaceState(null, "", "#" + sectionEl.id);
            }
          } catch (e) {
            /* noop */
          }
          openLoginDialog(registerSource);
          return;
        }
        checkPointsAndOpenConfirm(card);
      }

    function checkPointsAndOpenConfirm(card) {
      // 已登录 + 已激活：先刷新积分（避免使用页面初次加载的旧值），再判断是否足够
      var requiredPoints = parseInt(card.dataset.requiredPoints, 10) || 0;
      getUserPoints()
        .then(function (data) {
          var latestTotal =
            data && typeof data.total === "number" ? data.total : userTotalPoints;
          userTotalPoints = latestTotal;
          if (coinsValueEl) {
            coinsValueEl.textContent = latestTotal.toLocaleString();
          }
          if (requiredPoints > 0 && latestTotal < requiredPoints) {
            openInfoDialog(
              replaceTokens(insufficientMsgTpl, {
                need: requiredPoints,
                have: latestTotal,
              }),
            );
            return;
          }
          // 打开 Confirm 弹框
          openConfirm(card);
        })
        .catch(function () {
          // 接口失败时回退到缓存值，避免阻塞兑换流程
          if (requiredPoints > 0 && userTotalPoints < requiredPoints) {
            openInfoDialog(
              replaceTokens(insufficientMsgTpl, {
                need: requiredPoints,
                have: userTotalPoints,
              }),
            );
            return;
          }
          openConfirm(card);
        });
    }

    // ---- Confirm Redemption 弹框 ----
      function openConfirm(cardEl) {
        if (!confirmDlgEl) return;
        var titleEl = cardEl.querySelector(".bmpr-group__card-title");
        var productName = titleEl ? titleEl.textContent || "" : "";
        var requiredPoints = parseInt(cardEl.dataset.requiredPoints, 10) || 0;
        var productCode =
          cardEl.dataset.productCode || cardEl.dataset.productId || "";
        var imgEl = cardEl.querySelector(".bmpr-group__card-img");
        var productImage = imgEl ? imgEl.src : "";
        var productType = cardEl.dataset.productType || "7";

        pendingRedeem = {
        productCode: productCode,
        points: requiredPoints,
        name: productName,
        image: productImage,
        productType: productType,
      };

      // 渲染主标题 token
      var mainTplEl = confirmDlgEl.querySelector("[data-bmpr-confirm-main]");
      if (mainTplEl) {
        var mainTpl = mainTplEl.dataset.bmprTpl || mainTplEl.textContent;
        mainTplEl.textContent = replaceTokens(mainTpl, {
          itemName: productName,
          cozycoins: requiredPoints,
        });
      }

      // 替换 Confirm 弹框的图片。
      // 优先级：block.card_image（运营为弹框单独配的图） > 商品卡当前显示的图
      // 注：商品卡本身用的是 fallback_image → defaultCardImage → API 优先级
      var dialogImage = cardEl.dataset.cardImage || productImage;
      if (dialogImage) {
        var confirmImgWrap = confirmDlgEl.querySelector(
          "[data-bmpr-confirm-img]",
        );
        if (confirmImgWrap) {
          confirmImgWrap.innerHTML =
            '<img src="' +
            escapeAttr(dialogImage) +
            '" alt="' +
            escapeHtml(productName || "Product image") +
            '" loading="lazy" decoding="async">';
        }
      }

      openDialog(confirmDlgEl, cardEl.querySelector("[data-bmpr-redeem-btn]"));
    }

    // ---- Confirm 按钮：实际调兑换接口 ----
    var confirmBtn =
      confirmDlgEl && confirmDlgEl.querySelector("[data-bmpr-confirm-btn]");
    if (confirmBtn) {
      confirmBtn.addEventListener("click", function () {
        if (!pendingRedeem || !pendingRedeem.productCode) return;
        confirmBtn.disabled = true;
        var origLabel = confirmBtn.textContent;
        confirmBtn.textContent = "Processing...";
        exchangeProduct(pendingRedeem.productCode, 1, pendingRedeem.points)
          .then(function () {
            closeDialog(confirmDlgEl);
            // 刷新用户积分
            loadUserPoints().then(function () {
              openSuccess(pendingRedeem);
            });
          })
          .catch(function (err) {
            var code = err.serverCode;
            var isRedeemStatusFail = err.redeemStatus === 1;
            var msg =
              (code != null && redeemErrorMessages[code]) ||
              err.serverMsg ||
              err.message ||
              "Redeem failed";

            if (
              isRedeemStatusFail &&
              pendingRedeem &&
              pendingRedeem.productType
            ) {
              // 复用 init 阶段 loadAllProducts 缓存的全量商品列表
              // 不再额外发 getProducts，只需 getUserPoints 刷新积分
              getUserPoints()
                .then(function (pointsRes) {
                  var list = cachedProductList || [];
                  var codeStr = String(pendingRedeem.productCode || "");
                  var matched = null;
                  for (var i = 0; i < list.length; i++) {
                    var item = list[i];
                    if (!item) continue;
                    if (
                      item.productCode != null &&
                      String(item.productCode) === codeStr
                    ) {
                      matched = item;
                      break;
                    }
                    if (
                      item.shopifyProductId != null &&
                      String(item.shopifyProductId) === codeStr
                    ) {
                      matched = item;
                      break;
                    }
                  }
                  var need =
                    matched && typeof matched.exchangePoint === "number"
                      ? matched.exchangePoint
                      : pendingRedeem.points;
                  var have =
                    pointsRes && typeof pointsRes.total === "number"
                      ? pointsRes.total
                      : userTotalPoints;
                  openInfoDialog(
                    replaceTokens(insufficientMsgTpl, {
                      need: need,
                      have: have,
                    }),
                  );
                })
                .catch(function () {
                  alert(msg);
                });
            } else {
              alert(msg);
            }
          })
          .then(function () {
            confirmBtn.disabled = false;
            confirmBtn.textContent = origLabel;
          });
      });
    }

    // ---- 通用信息提示弹框 ----
    function openInfoDialog(message) {
      if (!infoDlgEl) return;
      var msgEl = infoDlgEl.querySelector("[data-bmpr-info-msg]");
      if (msgEl) msgEl.textContent = message || "";
      openDialog(infoDlgEl, null);
    }

    // ---- Reward Unlocked 弹框 ----
    function openSuccess(redeemInfo) {
      if (!successDlgEl) return;
      var mainTplEl = successDlgEl.querySelector("[data-bmpr-success-main]");
      if (mainTplEl) {
        var mainTpl = mainTplEl.dataset.bmprTpl || mainTplEl.textContent;
        mainTplEl.textContent = replaceTokens(mainTpl, {
          itemName: redeemInfo.name,
          cozycoins: redeemInfo.points,
        });
      }
      openDialog(successDlgEl, null);
    }

    var successBtn =
      successDlgEl && successDlgEl.querySelector("[data-bmpr-success-btn]");
    if (successBtn) {
      successBtn.addEventListener("click", function () {
        closeDialog(successDlgEl);
        // 优先用 section schema 配的 success_btn_url;
        // 只有运营没配时,才回落到 accountSiteUrl 兜底
        var configuredUrl = (config.successBtnUrl || "").trim();
        var accountSiteUrl =
          window.__MC__CONFIG__ && window.__MC__CONFIG__.accountSiteUrl
            ? window.__MC__CONFIG__.accountSiteUrl.replace(/\/$/, "")
            : "";
        var url = configuredUrl || accountSiteUrl || "";
        if (url) {
          window.location.href = url;
        }
      });
    }

    // ---- 启动 ----
    initLoginState();
    // 登录成功刷新后，自动滚回该组件
    scrollToSectionIfFromLogin(sectionEl, sectionId);
    // 整 section 只发一次 getProducts（把所有 card 的 productType 合并去重后请求）
    // initLoginState 已发了一次 getUserPoints（pageSize=1）取总积分
    // 整个初始化阶段对外只发 2 次接口
    var productLoadPromise = loadAllProducts();
    // 兜底：5s 后无论 API 是否完成都强制隐藏，避免骨架卡住
    var fallbackTimer = setTimeout(function () {
      console.warn("[BMPR] skeleton fallback timeout, hiding anyway");
      hideBmprPageSkeleton(sectionEl);
    }, 5000);
    if (productLoadPromise) {
      productLoadPromise
        .catch(function () {
          /* loadAllProducts 内部已处理每个分支的错误 */
        })
        .finally(function () {
          clearTimeout(fallbackTimer);
          hideBmprPageSkeleton(sectionEl);
        });
    } else {
      // 没有任何商品卡：立即隐藏骨架屏
      clearTimeout(fallbackTimer);
      hideBmprPageSkeleton(sectionEl);
    }

    // 把主标题原始模板写到 data 属性，openConfirm 时按需 token 替换
    var confirmMainTplEl =
      confirmDlgEl && confirmDlgEl.querySelector("[data-bmpr-confirm-main]");
    if (confirmMainTplEl && !confirmMainTplEl.dataset.bmprTpl) {
      confirmMainTplEl.dataset.bmprTpl = confirmMainTplEl.textContent;
    }
    var successMainTplEl =
      successDlgEl && successDlgEl.querySelector("[data-bmpr-success-main]");
    if (successMainTplEl && !successMainTplEl.dataset.bmprTpl) {
      successMainTplEl.dataset.bmprTpl = successMainTplEl.textContent;
    }
  }

  // --------------------------------------------------------------------------
  // 工具：HTML 转义
  // --------------------------------------------------------------------------
  function escapeAttr(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;");
  }
  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // --------------------------------------------------------------------------
  // DOM Ready 启动
  // --------------------------------------------------------------------------
  function boot() {
    var sections = document.querySelectorAll("[data-bmpr-section]");
    if (!sections.length) return;
    sections.forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
